import { useEffect, useState, type ReactNode } from "react";
import type { Field, Row } from "../types";
import { IconClose } from "../icons";

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  ncs: Row[]; // para selects com ref (Não Conformidades)
  onCancel: () => void;
  /** `acao` vem do botão extra do rodapé (ex.: "enviar"); no Salvar comum, undefined. */
  onSubmit: (data: Row, acao?: string) => Promise<void>;
  /** Conteúdo depois dos campos (ex.: arquivo e aprovação do documento). */
  children?: ReactNode;
  /** Segundo botão que também salva e segue para outra ação (ex.: enviar para revisão). */
  acaoExtra?: { acao: string; rotulo: string; rotuloSalvando: string } | null;
  /** Texto do botão que fecha sem salvar (padrão "Cancelar"). */
  rotuloFechar?: string;
  /** Pop-up mais largo (ex.: documento, que traz o bloco de aprovação). */
  largo?: boolean;
  /**
   * Ajusta um campo na hora de desenhar, com o que já foi preenchido — ex.: as
   * opções de tipo e setor vindas da configuração, ou a dica do próximo código
   * do tipo escolhido. Os `fields` em si não mudam (mudá-los zeraria o form).
   */
  ajustarCampo?: (f: Field, form: Row) => Field;
}

/** Rótulo de uma NC no select (código + título). */
function ncLabel(nc: Row): string {
  const codigo = nc.codigo ? `${nc.codigo} — ` : "";
  return `${codigo}${nc.titulo ?? ""}`;
}

/** Modal de cadastro/edição gerado a partir da definição de campos. */
export default function RecordForm({
  title,
  fields,
  initial,
  ncs,
  onCancel,
  onSubmit,
  children,
  acaoExtra,
  rotuloFechar = "Cancelar",
  largo = false,
  ajustarCampo,
}: Props) {
  const [form, setForm] = useState<Row>({});
  // Qual botão está salvando (o comum = "salvar"), para só ele mostrar "Salvando...".
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Campos "computed" (ex.: gestor do setor) vêm do backend — exibidos, não editados.
  const editable = fields.filter((f) => !f.computed);

  useEffect(() => {
    const base: Row = {};
    for (const f of editable) {
      let v = initial ? initial[f.name] : "";
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      base[f.name] = v ?? "";
    }
    setForm(base);
    setError(null);
  }, [initial, fields]);

  const set = (name: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // O botão que enviou o form diz a ação. Enter num campo usa o Salvar comum
    // (o primeiro botão de envio), então nunca dispara a ação extra sem querer.
    const botao = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
    const acao = botao?.dataset.acao || undefined;
    setSaving(acao ?? "salvar");
    setError(null);
    try {
      await onSubmit(form, acao);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className={largo ? "modal-card modal-lg" : "modal-card"} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {editable.map((campo) => ajustarCampo?.(campo, form) ?? campo).map((f) => (
              <label
                key={f.name}
                className={f.type === "textarea" ? "field field-wide" : "field"}
              >
                <span>
                  {f.label}
                  {f.required && <em className="req"> *</em>}
                </span>
                {renderInput(f, form[f.name], set, ncs)}
              </label>
            ))}
          </div>

          {children}
        </form>

        <div className="modal-foot">
          {error && <div className="form-error">{error}</div>}
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {rotuloFechar}
          </button>
          <button
            type="submit"
            form="record-form"
            className={acaoExtra ? "btn-secondary" : "btn-primary"}
            disabled={saving !== null}
          >
            {saving === "salvar" ? "Salvando..." : "Salvar"}
          </button>
          {acaoExtra && (
            <button
              type="submit"
              form="record-form"
              className="btn-primary"
              data-acao={acaoExtra.acao}
              disabled={saving !== null}
            >
              {saving === acaoExtra.acao ? acaoExtra.rotuloSalvando : acaoExtra.rotulo}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  ncs: Row[],
) {
  const v = (value ?? "") as string | number;

  if (f.ref === "nao_conformidades") {
    return (
      <select
        value={v}
        required={f.required}
        onChange={(e) => set(f.name, e.target.value)}
      >
        <option value="">Selecione...</option>
        {ncs.map((nc) => (
          <option key={nc.id} value={nc.id}>
            {ncLabel(nc)}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "select": {
      if (f.grupos) {
        // Mesma regra de baixo: o valor gravado que não está na lista continua aparecendo.
        const naLista = f.grupos.some((g) => g.opcoes.some((o) => o.valor === String(v)));
        return (
          <select value={v} required={f.required} onChange={(e) => set(f.name, e.target.value)}>
            <option value="">Selecione...</option>
            {v !== "" && !naLista && <option value={String(v)}>{String(v)}</option>}
            {f.grupos.map((g) => (
              <optgroup key={g.rotulo} label={g.rotulo}>
                {g.opcoes.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.rotulo}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        );
      }
      // Valor gravado que saiu da lista (ex.: setor antigo) continua aparecendo
      // em vez de o campo parecer vazio e ser trocado sem a pessoa perceber.
      const opcoes = f.options ?? [];
      const lista = v !== "" && !opcoes.includes(String(v)) ? [...opcoes, String(v)] : opcoes;
      return (
        <select
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        >
          <option value="">Selecione...</option>
          {lista.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    case "textarea":
      return (
        <textarea
          rows={3}
          value={v}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          step="any"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "currency":
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          required={f.required}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          value={v}
          required={f.required}
          placeholder={f.placeholder}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
  }
}
